import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import LogoutIcon from '@mui/icons-material/Logout';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SaveIcon from '@mui/icons-material/Save';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import BreadCrumbs from 'components/layouts/BreadCrumbs';
import UIContainer from 'components/ui/UIContainer';
import AchievementsContent from 'pages/AchievementsPage/AchievementsContent';
import VocabularyCompositionCard from 'pages/HomePage/components/VocabularyCompositionCard';
import { MOCK_STATS } from 'pages/HomePage/mockStats';
import { axiosInstance } from 'setup/axiosInstance';
import {
  TableColumnSettings,
  TableSettingsKey,
  UserProfile,
  VocabularyTypeRecord,
} from 'app/types';
import { useAppSelector } from 'app/hooks';
import { AuthContext } from 'AuthProvider';
import TableSettingsDialog from 'components/TableSettingsDialog';
import { createDefaultColumnSettings } from 'components/tableSettings';
import { useTableSettings } from 'hooks/useTableSettings';
import { useSearchParams } from 'react-router-dom';

type ProfileSection =
  | 'profile'
  | 'awards'
  | 'stats'
  | 'subscription'
  | 'settings';

const isProfileSection = (value: string | null): value is ProfileSection =>
  value === 'profile' ||
  value === 'awards' ||
  value === 'stats' ||
  value === 'subscription' ||
  value === 'settings';

const EMPTY_PROFILE: UserProfile = {
  birthday: '',
  email: '',
  firstName: '',
  id: 0,
  lastName: '',
  nativeLanguage: 'uk',
};

const LANGUAGE_OPTIONS = [
  { label: 'Українська', value: 'uk' },
  { label: 'English', value: 'en' },
];

const PROFILE_MENU: Array<{
  icon: React.ReactNode;
  id: ProfileSection;
  label: string;
}> = [
  {
    icon: <AccountCircleOutlinedIcon fontSize={'small'} />,
    id: 'profile',
    label: 'Профіль',
  },
  {
    icon: <EmojiEventsOutlinedIcon fontSize={'small'} />,
    id: 'awards',
    label: 'Нагороди',
  },
  {
    icon: <BarChartOutlinedIcon fontSize={'small'} />,
    id: 'stats',
    label: 'Статистика',
  },
  {
    icon: <CreditCardOutlinedIcon fontSize={'small'} />,
    id: 'subscription',
    label: 'Підписка',
  },
  {
    icon: <SettingsOutlinedIcon fontSize={'small'} />,
    id: 'settings',
    label: 'Налаштування',
  },
];

const TABLE_SETTINGS_CONFIGS: Array<{
  columns: TableColumnSettings[];
  key: TableSettingsKey;
  title: string;
}> = [
  {
    key: 'VOCABULARY_WORDS',
    title: 'Мій словник слів',
    columns: createDefaultColumnSettings({
      number: true,
      word: true,
      transcription: true,
      translation: true,
      partOfSpeech: true,
      definition: true,
      isLearned: true,
      isActiveSpeech: true,
    }),
  },
  {
    key: 'VOCABULARY_EXPRESSIONS',
    title: 'Мій словник фраз і словосполучень',
    columns: createDefaultColumnSettings({
      number: true,
      word: true,
      transcription: true,
      translation: true,
      partOfSpeech: true,
      definition: true,
      isLearned: true,
      isActiveSpeech: true,
    }),
  },
  {
    key: 'VOCABULARY_IRREGULAR_VERBS',
    title: 'Мій словник неправильних дієслів',
    columns: createDefaultColumnSettings({
      number: true,
      infinitive: true,
      infinitiveTranscription: true,
      pastSimple: true,
      pastSimpleTranscription: true,
      pastParticiple: true,
      pastParticipleTranscription: true,
      translation: true,
      isLearned: true,
      isActiveSpeech: true,
    }).concat({
      key: 'description',
      order: 10,
      visible: false,
    }),
  },
  {
    key: 'DICTIONARY_WORDS',
    title: 'Dictionary words',
    columns: createDefaultColumnSettings({
      number: true,
      word: true,
      transcription: true,
      translation: true,
      partOfSpeech: true,
      definition: true,
    }),
  },
  {
    key: 'DICTIONARY_EXPRESSIONS',
    title: 'Dictionary expressions',
    columns: createDefaultColumnSettings({
      number: true,
      word: true,
      transcription: true,
      translation: true,
      partOfSpeech: true,
      definition: true,
    }),
  },
  {
    key: 'DICTIONARY_IRREGULAR_VERBS',
    title: 'Dictionary irregular verbs',
    columns: createDefaultColumnSettings({
      number: true,
      infinitive: true,
      infinitiveTranscription: true,
      pastSimple: true,
      pastSimpleTranscription: true,
      pastParticiple: true,
      pastParticipleTranscription: true,
      translation: true,
    }),
  },
];

const languageLabel = (value?: string) =>
  LANGUAGE_OPTIONS.find((option) => option.value === value)?.label ||
  value ||
  '-';

const fullName = (profile: UserProfile) =>
  [profile.firstName, profile.lastName].filter(Boolean).join(' ') ||
  profile.email ||
  'Мій профіль';

const sumVocabulary = (
  data: VocabularyTypeRecord[],
  key: keyof Pick<
    VocabularyTypeRecord,
    'inActiveSpeech' | 'learned' | 'needToLearn' | 'totalCount'
  >,
) => data.reduce((acc, record) => acc + Number(record[key] || 0), 0);

const ProfilePage: React.FC = () => {
  const { logout } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState<ProfileSection>('profile');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile>(EMPTY_PROFILE);
  const [profileDraft, setProfileDraft] = useState<UserProfile>(EMPTY_PROFILE);
  const [saving, setSaving] = useState(false);
  const [activeTableSettingsKey, setActiveTableSettingsKey] =
    useState<TableSettingsKey>(TABLE_SETTINGS_CONFIGS[0].key);
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(
    null,
  );
  const activeTableSettingsConfig =
    TABLE_SETTINGS_CONFIGS.find(({ key }) => key === activeTableSettingsKey) ||
    TABLE_SETTINGS_CONFIGS[0];
  const {
    resetSettings: resetTableSettings,
    saveSettings: saveTableSettings,
    settings: tableSettings,
  } = useTableSettings(
    activeTableSettingsConfig.key,
    activeTableSettingsConfig.columns,
  );

  useEffect(() => {
    const section = searchParams.get('section');

    if (isProfileSection(section)) {
      setActiveSection(section);
      setEditMode(false);
    }
  }, [searchParams]);

  // Same vocabulary roll-up as HomePage so the Composition card stays truthful
  // when shown here. Composition (CEFR breakdown) is still mock until the BE
  // exposes it per user.
  const vocabulary = useAppSelector((store) => store.common.vocabulary.data);
  const vocabularyTotals = useMemo(
    () => ({
      activeSpeech: sumVocabulary(vocabulary, 'inActiveSpeech'),
      learned: sumVocabulary(vocabulary, 'learned'),
    }),
    [vocabulary],
  );

  const initials = useMemo(() => {
    const nameParts = [profile.firstName, profile.lastName].filter(Boolean);

    if (!nameParts.length) {
      return profile.email.slice(0, 1).toUpperCase() || 'M';
    }

    return nameParts
      .map((part) => part.slice(0, 1))
      .join('')
      .toUpperCase();
  }, [profile.email, profile.firstName, profile.lastName]);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        const { data } = await axiosInstance.get<UserProfile>('/profile');
        const nextProfile = {
          ...EMPTY_PROFILE,
          ...data,
          birthday: data.birthday || '',
          nativeLanguage: data.nativeLanguage || 'uk',
        };

        setProfile(nextProfile);
        setProfileDraft(nextProfile);
      } catch (e) {
        setError('Не вдалося завантажити профіль.');
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  const startEdit = () => {
    setProfileDraft(profile);
    setEditMode(true);
  };

  const cancelEdit = () => {
    setProfileDraft(profile);
    setEditMode(false);
  };

  const saveProfile = async () => {
    try {
      setSaving(true);
      setError('');

      const { data } = await axiosInstance.put<UserProfile>('/profile', {
        birthday: profileDraft.birthday || null,
        firstName: profileDraft.firstName,
        lastName: profileDraft.lastName,
        nativeLanguage: profileDraft.nativeLanguage,
      });
      const nextProfile = {
        ...EMPTY_PROFILE,
        ...data,
        birthday: data.birthday || '',
        nativeLanguage: data.nativeLanguage || 'uk',
      };

      setProfile(nextProfile);
      setProfileDraft(nextProfile);
      window.dispatchEvent(
        new CustomEvent('profile-updated', { detail: nextProfile }),
      );
      setEditMode(false);
    } catch (e) {
      setError('Не вдалося зберегти профіль.');
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const deleteProfile = async () => {
    try {
      await axiosInstance.delete('/profile');
      logout();
    } catch (e) {
      setError('Не вдалося видалити профіль.');
      setDeleteDialogOpen(false);
      console.error(e);
    }
  };

  const updateDraft = (field: keyof UserProfile, value: string) => {
    setProfileDraft((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const renderProfileInfo = () => (
    <Stack spacing={2.5}>
      {error ? <Alert severity={'error'}>{error}</Alert> : null}

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 6 }}>
          <InfoRow
            icon={<MailOutlineIcon />}
            label={'Email'}
            value={profile.email || '-'}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <InfoRow
            icon={<LanguageIcon />}
            label={'Рідна мова'}
            value={languageLabel(profile.nativeLanguage)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <InfoRow label={"Ім'я"} value={profile.firstName || '-'} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <InfoRow label={'Прізвище'} value={profile.lastName || '-'} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <InfoRow label={'День народження'} value={profile.birthday || '-'} />
        </Grid>
      </Grid>

      <Divider />

      <Box>
        <Button
          color={'error'}
          onClick={() => setDeleteDialogOpen(true)}
          startIcon={<DeleteOutlineIcon />}
          variant={'outlined'}
        >
          {'Видалити профіль'}
        </Button>
      </Box>
    </Stack>
  );

  const renderProfileForm = () => (
    <Stack spacing={2.5}>
      {error ? <Alert severity={'error'}>{error}</Alert> : null}

      <TextField fullWidth disabled label={'Email'} value={profile.email} />

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label={"Ім'я"}
            value={profileDraft.firstName || ''}
            onChange={(event) => updateDraft('firstName', event.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label={'Прізвище'}
            value={profileDraft.lastName || ''}
            onChange={(event) => updateDraft('lastName', event.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            InputLabelProps={{ shrink: true }}
            label={'День народження'}
            type={'date'}
            value={profileDraft.birthday || ''}
            onChange={(event) => updateDraft('birthday', event.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label={'Рідна мова'}
            SelectProps={{
              MenuProps: {
                disableScrollLock: true,
              },
            }}
            select
            value={profileDraft.nativeLanguage || 'uk'}
            onChange={(event) =>
              updateDraft('nativeLanguage', event.target.value)
            }
          >
            {LANGUAGE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <Stack direction={'row'} spacing={1.5}>
        <Button
          disabled={saving}
          onClick={saveProfile}
          startIcon={<SaveIcon />}
        >
          {saving ? 'Збереження...' : 'Зберегти'}
        </Button>
        <Button disabled={saving} onClick={cancelEdit} variant={'outlined'}>
          {'Скасувати'}
        </Button>
      </Stack>
    </Stack>
  );

  const renderSettings = () => (
    <Stack spacing={3}>
      <Box>
        <Typography variant={'h3'}>{'Налаштування'}</Typography>
        <Typography color={'text.secondary'}>
          {'Персональні налаштування таблиць для словників і Dictionary.'}
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {TABLE_SETTINGS_CONFIGS.map((config) => (
          <Grid key={config.key} size={{ xs: 12, md: 6 }}>
            <Card variant={'outlined'} sx={{ height: '100%' }}>
              <CardContent>
                <Stack spacing={2}>
                  <Box>
                    <Typography fontWeight={700}>{config.title}</Typography>
                    <Typography color={'text.secondary'} variant={'body2'}>
                      {
                        'Порядок колонок, видимість колонок і підсвітка Learned / Active speech.'
                      }
                    </Typography>
                  </Box>
                  <Button
                    onClick={(e) => {
                      setActiveTableSettingsKey(config.key);
                      setSettingsAnchor(e.currentTarget);
                    }}
                    variant={'contained'}
                  >
                    {'Налаштувати'}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );

  const renderPlaceholder = () => {
    if (activeSection === 'awards') {
      return <AchievementsContent />;
    }

    if (activeSection === 'stats') {
      return (
        <Stack spacing={3}>
          <Box>
            <Typography variant={'h3'}>{'Статистика'}</Typography>
            <Typography color={'text.secondary'}>
              {
                'Зріз твого словникового запасу за рівнями CEFR. Більше графіків зʼявиться у наступних ітераціях.'
              }
            </Typography>
          </Box>

          <VocabularyCompositionCard
            composition={MOCK_STATS.composition}
            totalLearned={vocabularyTotals.learned || 312}
            totalActive={vocabularyTotals.activeSpeech || 184}
          />
        </Stack>
      );
    }

    if (activeSection === 'subscription') {
      return (
        <PlaceholderSection
          title={'Підписка'}
          items={[
            'Поточний статус: Free',
            'Історія активацій буде доступна пізніше',
          ]}
        />
      );
    }

    if (activeSection === 'settings') {
      return renderSettings();
    }

    return (
      <PlaceholderSection
        title={'Налаштування'}
        items={[
          'Кількість слів на сторінці: 20',
          'Детальні налаштування будуть доступні пізніше',
        ]}
      />
    );
  };

  return (
    <UIContainer sx={{ mb: 8 }}>
      <BreadCrumbs />

      <Grid container spacing={3} sx={{ mt: 3 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ minHeight: 540 }}>
            <CardContent>
              <Stack spacing={1}>
                {PROFILE_MENU.map((item) => {
                  const active = activeSection === item.id;

                  return (
                    <Button
                      key={item.id}
                      onClick={() => {
                        setActiveSection(item.id);
                        setSearchParams({ section: item.id });
                        setEditMode(false);
                      }}
                      startIcon={item.icon}
                      sx={{
                        justifyContent: 'flex-start',
                        backgroundColor: active
                          ? 'action.selected'
                          : 'transparent',
                        color: active ? 'primary.main' : 'text.primary',
                      }}
                      variant={'text'}
                    >
                      {item.label}
                    </Button>
                  );
                })}

                <Divider sx={{ my: 1 }} />

                <Button
                  color={'error'}
                  onClick={logout}
                  startIcon={<LogoutIcon />}
                  sx={{ justifyContent: 'flex-start' }}
                  variant={'text'}
                >
                  {'Вийти'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 9 }}>
          <Card sx={{ minHeight: 540, overflow: 'hidden' }}>
            <Box
              sx={{
                alignItems: 'flex-end',
                backgroundColor: 'primary.main',
                backgroundImage:
                  'radial-gradient(circle at 20% 20%, rgba(255,255,255,.18) 0 10%, transparent 11%), radial-gradient(circle at 80% 30%, rgba(255,255,255,.12) 0 9%, transparent 10%)',
                display: 'flex',
                minHeight: 150,
                px: 5,
                py: 3,
              }}
            >
              <Avatar
                sx={{
                  bgcolor: 'background.paper',
                  border: '4px solid',
                  borderColor: 'background.paper',
                  color: 'primary.main',
                  fontSize: 34,
                  height: 112,
                  mr: 2,
                  width: 112,
                }}
              >
                {initials}
              </Avatar>
              <Box sx={{ color: '#fff', mb: 1 }}>
                <Typography variant={'h2'}>{fullName(profile)}</Typography>
                <Typography>{profile.email}</Typography>
              </Box>
            </Box>

            <CardContent sx={{ p: 5 }}>
              {loading ? (
                <Typography>{'Завантаження профілю...'}</Typography>
              ) : activeSection === 'profile' ? (
                <Stack spacing={3}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent={'space-between'}
                    spacing={2}
                  >
                    <Box>
                      <Typography variant={'h3'}>{'Профіль'}</Typography>
                      <Typography color={'text.secondary'}>
                        {'Основна інформація користувача'}
                      </Typography>
                    </Box>

                    {!editMode ? (
                      <Button
                        onClick={startEdit}
                        startIcon={<EditOutlinedIcon />}
                        variant={'outlined'}
                      >
                        {'Редагувати профіль'}
                      </Button>
                    ) : null}
                  </Stack>

                  {editMode ? renderProfileForm() : renderProfileInfo()}
                </Stack>
              ) : (
                renderPlaceholder()
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        disableScrollLock
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{'Видалити профіль?'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {
              'Профіль буде деактивовано. Ви одразу вийдете з акаунта, а навчальна історія та словники залишаться в базі без фізичного видалення.'
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {'Скасувати'}
          </Button>
          <Button color={'error'} onClick={deleteProfile} variant={'contained'}>
            {'Видалити профіль'}
          </Button>
        </DialogActions>
      </Dialog>
      <TableSettingsDialog
        anchorEl={settingsAnchor}
        settings={tableSettings}
        title={activeTableSettingsConfig.title}
        onClose={() => setSettingsAnchor(null)}
        onReset={resetTableSettings}
        onSave={saveTableSettings}
      />
    </UIContainer>
  );
};

interface InfoRowProps {
  icon?: React.ReactNode;
  label: string;
  value: string;
}

const InfoRow: React.FC<InfoRowProps> = ({ icon, label, value }) => (
  <Stack direction={'row'} spacing={1.5}>
    {icon ? (
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: 'action.selected',
          borderRadius: 1.5,
          display: 'flex',
          height: 44,
          justifyContent: 'center',
          width: 44,
        }}
      >
        {icon}
      </Box>
    ) : null}
    <Box>
      <Typography color={'text.secondary'} fontWeight={600}>
        {label}
      </Typography>
      <Typography color={'primary.main'} fontWeight={700}>
        {value}
      </Typography>
    </Box>
  </Stack>
);

interface PlaceholderSectionProps {
  items: string[];
  title: string;
}

const PlaceholderSection: React.FC<PlaceholderSectionProps> = ({
  items,
  title,
}) => (
  <Stack spacing={3}>
    <Box>
      <Typography variant={'h3'}>{title}</Typography>
      <Typography color={'text.secondary'}>
        {'Цей розділ буде наповнений у наступних ітераціях.'}
      </Typography>
    </Box>

    <Grid container spacing={2}>
      {items.map((item) => (
        <Grid key={item} size={{ xs: 12, md: 4 }}>
          <Card variant={'outlined'} sx={{ height: '100%' }}>
            <CardContent>
              <Typography fontWeight={700}>{item}</Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  </Stack>
);

export default ProfilePage;
